import { getBrandContext, type BrandContext, type ToolExecutionPolicy } from "./types";
import type { BrandId } from "./brandRegistry";
import { resolveSnippetPolicy } from "./snippetPolicy";
import { GENERIC_SNIPPET_TYPES } from "./snippetStyle";
import { buildBrandIconSchemaDescription, getBrandIconIds } from "./ubsIconCatalog";
import { normalizeSelectedUserFileIds } from "../../../shared/researchSelection";
import { buildFactsetChartParametersSchema } from "../../../shared/factset/chartCreatorPayload";

type JsonSchema = Record<string, unknown>;

type OpenAIToolName =
  | "get_company_logo"
  | "finster_query"
  | "factset_ai_query"
  | "get_slide_images"
  | "apply_edits"
  | "get_slides_info"
  | "insert_library_slide"
  | "apply_preslink"
  | "list_slide_library_collections"
  | "search_slide_library"
  | "get_library_slide_previews"
  | "present_deck_plan"
  | "update_deck_plan"
  | "active_graph"
  | "list_active_graph_types"
  | "id_lookup";

export interface OpenAIFunctionToolDefinition {
  type: "function";
  name: OpenAIToolName;
  description: string;
  strict?: boolean;
  parameters: JsonSchema;
}

const PARAGRAPH_ALIGNMENT_ENUM = [
  "Left",
  "Center",
  "Right",
  "Justify",
  "JustifyLow",
  "Distributed",
  "ThaiDistributed",
];

const GEOMETRIC_TYPE_ENUM = [
  "rectangle",
  "ellipse",
  "triangle",
  "diamond",
  "pentagon",
  "hexagon",
  "star4",
  "star5",
  "star6",
  "roundedRectangle",
  "parallelogram",
  "trapezoid",
  "arrow",
  "arrowLeft",
  "arrowUp",
  "arrowDown",
  "callout",
  "cloud",
  "heart",
  "lightning",
  "bracePair",
  "bracketPair",
  "plus",
  "minus",
  "line",
  "straightConnector",
];

const PARAGRAPH_OP_TARGET_SCHEMA: JsonSchema = {
  oneOf: [
    {
      type: "object",
      additionalProperties: false,
      required: ["type"],
      properties: {
        type: { const: "all" },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["type", "indices"],
      properties: {
        type: { const: "indices" },
        indices: {
          type: "array",
          items: { type: "integer" },
        },
      },
    },
  ],
};

const PARAGRAPH_FONT_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  description:
    "Font for the targeted paragraph(s) or run. Do not rely on shape-level font as a fallback (the host applies shape-level font after paragraphOps): on a base target.type=\"all\" entry, set every font property this shape needs.",
  properties: {
    size: { type: "number", description: "Font size in points." },
    family: { type: "string", description: "Font family." },
    color: { type: "string", description: "Font color, normally #RRGGBB." },
    bold: { type: "boolean", description: "Bold state; preserve explicit false values." },
    italic: { type: "boolean", description: "Italic state; preserve explicit false values." },
  },
};

const TABLE_TARGET_SCHEMA: JsonSchema = {
  oneOf: [
    {
      type: "object",
      additionalProperties: false,
      required: ["type"],
      properties: {
        type: { const: "all" },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["type", "index"],
      properties: {
        type: { enum: ["row", "column"] },
        index: { type: "integer", minimum: 0 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["type", "row", "col"],
      properties: {
        type: { const: "cell" },
        row: { type: "integer", minimum: 0 },
        col: { type: "integer", minimum: 0 },
      },
    },
  ],
};

const TABLE_STRUCTURE_OP_SCHEMA: JsonSchema = {
  oneOf: [
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "index"],
      properties: {
        op: { const: "addRow" },
        index: { type: "integer", minimum: 0 },
        count: { type: "integer", minimum: 1 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "index"],
      properties: {
        op: { const: "deleteRow" },
        index: { type: "integer", minimum: 0 },
        count: { type: "integer", minimum: 1 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "index"],
      properties: {
        op: { const: "addColumn" },
        index: { type: "integer", minimum: 0 },
        count: { type: "integer", minimum: 1 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "index"],
      properties: {
        op: { const: "deleteColumn" },
        index: { type: "integer", minimum: 0 },
        count: { type: "integer", minimum: 1 },
      },
    },
    {
      type: "object",
      additionalProperties: false,
      required: ["op", "rowIndex", "columnIndex", "rowCount", "columnCount"],
      properties: {
        op: { enum: ["mergeCells", "splitCell", "resizeCellSpan"] },
        rowIndex: { type: "integer", minimum: 0 },
        columnIndex: { type: "integer", minimum: 0 },
        rowCount: { type: "integer", minimum: 1 },
        columnCount: { type: "integer", minimum: 1 },
      },
    },
  ],
};

// Three chart-type families are intentionally omitted from this OpenAI
// structured-output enum. Because the values aren't in the enum, the
// OpenAI model literally cannot emit them in structured-output mode.
// This is the *enforcement* layer; the matching prompt-text allow-lists
// (slideGenerationPrompts.ts, systemPrompts.ts, prompts.ts) document
// the same exclusions for the model's benefit on providers / paths
// that don't go through structured output.
//
//   1. Scatter (`xlXYScatter`, `xlXYScatterLines`,
//      `xlXYScatterLinesNoMarkers`) and bubble (`xlBubble`,
//      `xlBubble3DEffect`). The FactSet host's `Chart` op does not
//      assign series.xValues / series.values to the underlying
//      Series.XValues / Series.Values COM properties for these types,
//      and two related code paths throw COM errors
//      (`axes[xlCategory].enabled: false` → "Parameter not valid";
//      bare `legend: { hasLegend: false }` → "This object doesn't
//      have a legend"). Tracking: f2_docs/bugs/scatter-chart-data-not-applied/.
//
//   2. Doughnut (`xlDoughnut`, `xlDoughnutExploded`). The host's
//      `Chart` op silently ignores `legend: { hasLegend: false }` on
//      doughnut and renders with a default-on legend in the host's
//      default (large) font, dominating the chart area. `xlPie` is
//      verified unaffected — the defect is doughnut-specific — so
//      the model can substitute `xlPie` for any composition request.
//      Tracking: f2_docs/bugs/doughnut-haslegend-false-ignored/.
//
// Re-add each family to the enum once its respective host-build gap
// closes.
const XL_CHART_TYPE_ENUM = [
  "xlColumnClustered",
  "xlColumnStacked",
  "xlColumnStacked100",
  "xlBarClustered",
  "xlBarStacked",
  "xlBarStacked100",
  "xlLine",
  "xlLineMarkers",
  "xlLineStacked",
  "xlLineStackedMarkers",
  "xlPie",
  "xlPieExploded",
  "xlArea",
  "xlAreaStacked",
  "xlAreaStacked100",
  "xlRadar",
  "xlRadarFilled",
];

const XL_AXIS_TYPE_ENUM = ["xlCategory", "xlValue", "xlSeriesAxis"];
const XL_AXIS_GROUP_ENUM = ["xlPrimary", "xlSecondary"];
const XL_TICK_MARK_ENUM = [
  "xlTickMarkNone",
  "xlTickMarkInside",
  "xlTickMarkOutside",
  "xlTickMarkCross",
];
const XL_LEGEND_POSITION_ENUM = [
  "xlLegendPositionTop",
  "xlLegendPositionBottom",
  "xlLegendPositionLeft",
  "xlLegendPositionRight",
  "xlLegendPositionCorner",
];
const XL_MARKER_STYLE_ENUM = [
  "xlMarkerStyleAutomatic",
  "xlMarkerStyleNone",
  "xlMarkerStyleSquare",
  "xlMarkerStyleCircle",
  "xlMarkerStyleDiamond",
  "xlMarkerStyleTriangle",
  "xlMarkerStyleDash",
  "xlMarkerStyleDot",
  "xlMarkerStylePlus",
  "xlMarkerStyleStar",
  "xlMarkerStyleX",
];
const XL_DATA_LABEL_POSITION_ENUM = [
  "xlLabelPositionAbove",
  "xlLabelPositionBelow",
  "xlLabelPositionCenter",
  "xlLabelPositionLeft",
  "xlLabelPositionRight",
  "xlLabelPositionOutsideEnd",
  "xlLabelPositionInsideEnd",
  "xlLabelPositionInsideBase",
  "xlLabelPositionBestFit",
];

const CHART_FONT_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    name: { type: "string" },
    size: { type: "number" },
    bold: { type: "boolean" },
    italic: { type: "boolean" },
    underline: { type: "boolean" },
    colorRgb: { type: "string" },
  },
};

const CHART_FILL_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    isVisible: { type: "boolean" },
    colorRgb: {
      type: "string",
      description: "6-digit hex WITHOUT '#'.",
    },
  },
};

const CHART_LINE_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    isVisible: { type: "boolean" },
    colorRgb: {
      type: "string",
      description:
        "Stroke, 6-digit hex WITHOUT '#'. Required for xlLine/xlLineMarkers in addition to fill.colorRgb (same hex). Fill-only line series stay Office default blue.",
    },
    weight: { type: "number" },
    dashStyle: { type: "string" },
  },
};

const CHART_AREA_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    fill: CHART_FILL_SCHEMA,
    line: CHART_LINE_SCHEMA,
    font: CHART_FONT_SCHEMA,
  },
};

const PLOT_AREA_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    fill: CHART_FILL_SCHEMA,
    line: CHART_LINE_SCHEMA,
  },
};

const CHART_MARKER_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    style: { type: "string", enum: XL_MARKER_STYLE_ENUM },
    size: { type: "number" },
    fill: CHART_FILL_SCHEMA,
    line: CHART_LINE_SCHEMA,
  },
};

const CHART_DATA_LABELS_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    showValue: { type: "boolean" },
    showCategoryName: { type: "boolean" },
    showSeriesName: { type: "boolean" },
    showPercentage: { type: "boolean" },
    showBubbleSize: { type: "boolean" },
    showLegendKey: { type: "boolean" },
    position: { type: "string", enum: XL_DATA_LABEL_POSITION_ENUM },
    numberFormat: { type: "string" },
    separator: { type: "string" },
    font: CHART_FONT_SCHEMA,
    fill: CHART_FILL_SCHEMA,
    border: CHART_LINE_SCHEMA,
  },
};

const CHART_TITLE_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    hasTitle: { type: "boolean" },
    text: { type: "string" },
    includeInLayout: { type: "boolean" },
    font: CHART_FONT_SCHEMA,
  },
};

const CHART_LEGEND_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    hasLegend: { type: "boolean" },
    position: { type: "string", enum: XL_LEGEND_POSITION_ENUM },
    includeInLayout: { type: "boolean" },
    font: CHART_FONT_SCHEMA,
  },
};

const CHART_POINT_EDIT_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["index"],
  properties: {
    index: { type: "integer" },
    fill: CHART_FILL_SCHEMA,
    line: CHART_LINE_SCHEMA,
    explosion: { type: "integer" },
    hasDataLabel: { type: "boolean" },
    dataLabel: CHART_DATA_LABELS_SCHEMA,
  },
};

const CHART_SERIES_EDIT_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["index"],
  properties: {
    index: { type: "integer" },
    name: { type: "string" },
    // categories/values/xValues/bubbleSizes are stringified JSON arrays.
    categories: { type: "string" },
    values: { type: "string" },
    xValues: { type: "string" },
    bubbleSizes: { type: "string" },
    chartType: { type: "string", enum: XL_CHART_TYPE_ENUM },
    axisGroup: { type: "string", enum: XL_AXIS_GROUP_ENUM },
    plotOrder: { type: "integer" },
    smooth: { type: "boolean" },
    explosion: { type: "integer" },
    invertIfNegative: { type: "boolean" },
    hasDataLabels: { type: "boolean" },
    fill: CHART_FILL_SCHEMA,
    line: CHART_LINE_SCHEMA,
    marker: CHART_MARKER_SCHEMA,
    dataLabels: CHART_DATA_LABELS_SCHEMA,
    points: {
      type: "array",
      items: CHART_POINT_EDIT_SCHEMA,
    },
  },
};

const CHART_AXIS_EDIT_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["axisType"],
  properties: {
    axisType: { type: "string", enum: XL_AXIS_TYPE_ENUM },
    axisGroup: { type: "string", enum: XL_AXIS_GROUP_ENUM },
    enabled: { type: "boolean" },
    hasTitle: { type: "boolean" },
    titleText: { type: "string" },
    titleFont: CHART_FONT_SCHEMA,
    minimumScale: { type: "number" },
    maximumScale: { type: "number" },
    majorUnit: { type: "number" },
    minorUnit: { type: "number" },
    hasMajorGridlines: { type: "boolean" },
    hasMinorGridlines: { type: "boolean" },
    reversePlotOrder: { type: "boolean" },
    numberFormat: { type: "string" },
    tickLabelFont: CHART_FONT_SCHEMA,
    majorTickMark: { type: "string", enum: XL_TICK_MARK_ENUM },
    minorTickMark: { type: "string", enum: XL_TICK_MARK_ENUM },
    axisLine: CHART_LINE_SCHEMA,
    majorGridLine: CHART_LINE_SCHEMA,
    minorGridLine: CHART_LINE_SCHEMA,
  },
};

const CHART_GROUP_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["index"],
  properties: {
    index: { type: "integer" },
    gapWidth: { type: "integer" },
    overlap: { type: "integer" },
    varyByCategories: { type: "boolean" },
    firstSliceAngle: { type: "integer" },
    doughnutHoleSize: { type: "integer" },
    splitType: { type: "string" },
    splitValue: { type: "number" },
    secondPlotSize: { type: "integer" },
    bubbleScale: { type: "integer" },
    showNegativeBubbles: { type: "boolean" },
    sizeRepresents: { type: "string" },
    hasSeriesLines: { type: "boolean" },
    hasDropLines: { type: "boolean" },
    hasHiLoLines: { type: "boolean" },
    hasUpDownBars: { type: "boolean" },
    axisGroup: { type: "string", enum: XL_AXIS_GROUP_ENUM },
  },
};

const CHART_EDIT_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    title: CHART_TITLE_SCHEMA,
    legend: CHART_LEGEND_SCHEMA,
    series: {
      type: "array",
      items: CHART_SERIES_EDIT_SCHEMA,
    },
    axes: {
      type: "array",
      items: CHART_AXIS_EDIT_SCHEMA,
    },
    chartArea: CHART_AREA_SCHEMA,
    plotArea: PLOT_AREA_SCHEMA,
  },
};

const CHART_INFO_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  properties: {
    type: { type: "string", enum: ["Chart"] },
    id: { type: "integer" },
    index: { type: "integer" },
    name: { type: "string" },
    chartType: { type: "string", enum: XL_CHART_TYPE_ENUM },
    title: CHART_TITLE_SCHEMA,
    legend: CHART_LEGEND_SCHEMA,
    series: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["name", "values"],
        properties: {
          index: { type: "integer" },
          name: { type: "string" },
          // categories/values/xValues/bubbleSizes are stringified JSON arrays
          // (e.g. "[\"Q1\",\"Q2\"]" or "[120.4,135.2]") matching the f2_docs
          // golden wire format.
          categories: { type: "string" },
          values: { type: "string" },
          xValues: { type: "string" },
          bubbleSizes: { type: "string" },
          chartType: { type: "string", enum: XL_CHART_TYPE_ENUM },
          axisGroup: { type: "string", enum: XL_AXIS_GROUP_ENUM },
          plotOrder: { type: "integer" },
          smooth: { type: "boolean" },
          explosion: { type: "integer" },
          invertIfNegative: { type: "boolean" },
          hasDataLabels: { type: "boolean" },
          fill: CHART_FILL_SCHEMA,
          line: CHART_LINE_SCHEMA,
          marker: CHART_MARKER_SCHEMA,
          dataLabels: CHART_DATA_LABELS_SCHEMA,
          points: {
            type: "array",
            items: {
              type: "object",
              additionalProperties: false,
              required: ["index"],
              properties: {
                index: { type: "integer" },
                fill: CHART_FILL_SCHEMA,
                line: CHART_LINE_SCHEMA,
                explosion: { type: "integer" },
                hasDataLabel: { type: "boolean" },
                dataLabel: CHART_DATA_LABELS_SCHEMA,
              },
            },
          },
        },
      },
    },
    axes: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["axisType"],
        properties: {
          axisType: { type: "string", enum: XL_AXIS_TYPE_ENUM },
          axisGroup: { type: "string", enum: XL_AXIS_GROUP_ENUM },
          enabled: { type: "boolean" },
          hasTitle: { type: "boolean" },
          titleText: { type: "string" },
          titleFont: CHART_FONT_SCHEMA,
          minimumScale: { type: "number" },
          maximumScale: { type: "number" },
          majorUnit: { type: "number" },
          minorUnit: { type: "number" },
          hasMajorGridlines: { type: "boolean" },
          hasMinorGridlines: { type: "boolean" },
          reversePlotOrder: { type: "boolean" },
          numberFormat: { type: "string" },
          tickLabelFont: CHART_FONT_SCHEMA,
          majorTickMark: { type: "string", enum: XL_TICK_MARK_ENUM },
          minorTickMark: { type: "string", enum: XL_TICK_MARK_ENUM },
          axisLine: CHART_LINE_SCHEMA,
        },
      },
    },
    position: {
      type: "object",
      additionalProperties: false,
      required: ["left", "top", "width", "height"],
      properties: {
        left: { type: "number" },
        top: { type: "number" },
        width: { type: "number" },
        height: { type: "number" },
      },
    },
    chartGroups: {
      type: "array",
      items: CHART_GROUP_SCHEMA,
    },
    doughnutHoleSize: { type: "integer" },
    firstSliceAngle: { type: "integer" },
    gapWidth: { type: "integer" },
    overlap: { type: "integer" },
  },
};

const SHAPE_MODIFICATION_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["id", "action"],
  properties: {
    id: {
      type: "string",
      description:
        "Shape id from slidesInfo. Top-level shapes (no parentGroupId) are valid modify/delete targets. Child ids nested under type:\"group\".shapes[] cannot be modified or deleted — use the grouped-shape recipe.",
    },
    action: { type: "string", enum: ["create", "modify", "delete"] },
    placeholderName: {
      type: "string",
      description:
        "Layout-placeholder reference for shapes that FILL a placeholder on a slide you are inserting (slide action=\"insert\"). Set it to the placeholder's name from deckLayoutCatalog.layouts[*].placeholders[*].name. The runtime inserts the slide, re-extracts its real placeholders, uses this name to select the intended catalog placeholder, then maps to the generated placeholder by placeholder type and geometry — so it fills the placeholder instead of overlaying a new shape. Geometry is inherited from the placeholder, so you may omit left/top/width/height. If the same placeholderName appears multiple times in a layout, emit fills in visual reading order (top-to-bottom, left-to-right). Do NOT set this on existing-slide edits; use the real shape id instead.",
    },
    text: {
      type: "string",
      description:
        "On action=\"modify\", full text replacement loses existing paragraph/run formatting. Never use it when observed paragraphs[] include bulletVisible:true; use textReplacements instead. Full text content of the shape. MAY optionally contain inline Finster citation markup of the form <u id=\"<uuid>\" data-datasource=\"<DATA_SOURCE>\" data-type=\"<TYPE>\">VALUE</u> around values returned by finster_query or present in the slide-generation grounding context. Inline markup is a per-shape rendering hint only — the runtime strips it before writing to PowerPoint, so it never affects what the user sees on the slide. Inline markup is NEVER a substitute for declaring the citation in the top-level citations[] array. Do NOT rephrase, pad, or expand slide text just to make an inline tag fit naturally; choose the phrasing the slide needs and declare the citation in citations[].",
    },
    textReplacements: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["find", "replace"],
        properties: {
          find: { type: "string" },
          replace: {
            type: "string",
            description:
              "Replacement string. MAY optionally contain inline Finster <u id=\"…\" data-datasource=\"…\" data-type=\"…\">VALUE</u> markup around values returned by finster_query or present in the slide-generation grounding context. Inline markup is a per-shape rendering hint only — the runtime strips it before applying the replacement. Inline markup is NEVER a substitute for declaring the citation in the top-level citations[] array; declare the citation regardless of whether you wrapped the value inline.",
          },
        },
      },
    },
    paragraphOps: {
      type: "array",
      description:
        "Per-paragraph and per-substring formatting. When creating/recreating a mixed-format shape or explicitly changing its formatting, put the WHOLE shape's font here (a target.type=\"all\" base entry plus narrower indices/runs overrides) and do NOT also set shape-level textColor/fontSize/fontFamily/bold/italic: the host applies shape-level font to the whole shape AFTER paragraphOps and would overwrite these values. Target indices come from observed paragraphs[].index values. set.horizontalAlignment here is the ONLY alignment the host applies (shape-level horizontalAlignment is ignored).",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["target", "set"],
        properties: {
          target: PARAGRAPH_OP_TARGET_SCHEMA,
          set: {
            type: "object",
            additionalProperties: false,
            properties: {
              horizontalAlignment: { type: "string", enum: PARAGRAPH_ALIGNMENT_ENUM },
              indentLevel: {
                type: "integer",
                description:
                  "Outline level, 1-indexed: 1 = top-level (no indent), 2 = first sub-bullet, 3 = second sub-bullet (max 9). To nest, keep all lines in ONE shape and raise indentLevel on the sub-lines; never split into multiple text boxes.",
              },
              bullet: {
                type: "object",
                additionalProperties: false,
                description:
                  "Omit on modify when paragraphs[] already have bulletVisible:true. Set only when creating a list or changing list type (None/Numbered/Unnumbered).",
                properties: {
                  visible: { type: "boolean" },
                  type: { type: "string", enum: ["None", "Numbered", "Unnumbered"] },
                },
              },
              font: PARAGRAPH_FONT_SCHEMA,
              text: {
                type: "string",
                description:
                  "Replace ONLY the targeted paragraph(s) text. Omit to keep existing text and format only. Do not use with target.type=\"all\" to set different text per paragraph.",
              },
              runs: {
                type: "array",
                description:
                  "Most-specific substring formatting within the targeted paragraph. Character offsets are 0-based within exact paragraph text. Combine with set.font for a paragraph base plus substring overrides. Use observed run boundaries when available or calculate offsets from exact text; never infer offsets from a screenshot.",
                items: {
                  type: "object",
                  additionalProperties: false,
                  required: ["start", "length"],
                  properties: {
                    start: { type: "integer", minimum: 0 },
                    length: { type: "integer", minimum: 1 },
                    font: PARAGRAPH_FONT_SCHEMA,
                  },
                },
              },
            },
          },
        },
      },
    },
    left: { type: "number" },
    top: { type: "number" },
    width: { type: "number" },
    height: { type: "number" },
    geometricType: { type: "string", enum: GEOMETRIC_TYPE_ENUM },
    fillColor: { type: "string" },
    lineColor: { type: "string" },
    lineWidth: { type: "number" },
    textColor: {
      type: "string",
      description:
        "Whole-shape text color, applied by the host AFTER paragraphOps. Set ONLY when the entire shape is one color and you set no font via paragraphOps; if paragraphs differ or you use paragraphOps.set.font, OMIT this and set every color through paragraphOps, otherwise the whole shape is forced to this color.",
    },
    fontSize: {
      type: "number",
      description:
        "Whole-shape font size in points, applied AFTER paragraphOps. Set ONLY for a uniformly sized shape with no paragraphOps font; otherwise omit and set sizes via paragraphOps.set.font.",
    },
    fontFamily: {
      type: "string",
      description:
        "Whole-shape font family, applied AFTER paragraphOps. Set ONLY for a uniform shape with no paragraphOps font; otherwise omit and set family via paragraphOps.set.font.",
    },
    bold: {
      type: "boolean",
      description:
        "Whole-shape bold state, applied AFTER paragraphOps. Set ONLY for a uniform shape with no paragraphOps font; otherwise omit and set bold via paragraphOps.set.font, or every paragraph is forced to this weight.",
    },
    italic: {
      type: "boolean",
      description:
        "Whole-shape italic state, applied AFTER paragraphOps. Set ONLY for a uniform shape with no paragraphOps font; otherwise omit and set italic via paragraphOps.set.font.",
    },
    verticalAlignment: { type: "string", enum: ["top", "middle", "bottom"] },
    imageUrl: {
      type: "string",
      description:
        "External or non-logo image URL. For company logos from get_company_logo, use logoCompanyName with logoCandidateRef instead of pasting a URL.",
    },
    logoCompanyName: {
      type: "string",
      description:
        "Requested company name from a prior get_company_logo result. On a selected logo create, copy result.companyName exactly and pair it with the chosen candidate.logoCandidateRef; it is also used as fallback text if the ref cannot resolve.",
    },
    logoCandidateRef: {
      type: "string",
      description:
        "Opaque ref copied exactly from a chosen get_company_logo candidate. Set only with logoCompanyName on a create shape; runtime resolves the selected candidate without exposing its URL.",
    },
    table: {
      type: "object",
      additionalProperties: false,
      required: ["action"],
      properties: {
        action: { type: "string", enum: ["create", "edit", "replace"] },
        targetShapeId: { type: "string" },
        rows: {
          type: "array",
          items: {
            type: "array",
            items: { type: "string" },
          },
        },
        columnWidths: {
          type: "array",
          items: { type: "number" },
        },
        rowHeights: {
          type: "array",
          items: { type: "number" },
        },
        mergedAreas: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["rowIndex", "columnIndex", "rowCount", "columnCount"],
            properties: {
              rowIndex: { type: "integer" },
              columnIndex: { type: "integer" },
              rowCount: { type: "integer" },
              columnCount: { type: "integer" },
            },
          },
        },
        textUpdates: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["row", "col", "text"],
            properties: {
              row: { type: "integer", minimum: 0 },
              col: { type: "integer", minimum: 0 },
              text: {
                type: "string",
                description:
                  "New cell text. MAY optionally contain inline Finster <u id=\"…\" data-datasource=\"…\" data-type=\"…\">VALUE</u> citation markup copied from finster_query output or from the slide-generation grounding context. Inline markup is a per-cell rendering hint only — the runtime strips it before writing the cell. Inline markup is NEVER a substitute for declaring the citation in the top-level citations[] array; declare the citation regardless of whether you wrapped the value inline.",
              },
            },
          },
        },
        styleUpdates: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["target", "set"],
            properties: {
              target: TABLE_TARGET_SCHEMA,
              set: {
                type: "object",
                additionalProperties: true,
              },
            },
          },
        },
        structureOps: {
          type: "array",
          items: TABLE_STRUCTURE_OP_SCHEMA,
        },
        clearOps: {
          type: "array",
          items: {
            type: "object",
            additionalProperties: false,
            required: ["mode"],
            properties: {
              mode: { type: "string", enum: ["all", "text_only", "format_only"] },
            },
          },
        },
      },
    },
    // In-slide native PowerPoint chart for create/full replacement workflows.
    // Wire format matches f2_docs/charting/operations.*.json goldens.
    chart: CHART_INFO_SCHEMA,
    // Existing native PowerPoint chart patch. shape.action MUST be "modify";
    // only title/legend are host-verified in-place chart edits.
    chartEdit: CHART_EDIT_SCHEMA,
  },
};

const SNIPPET_SCHEMA: JsonSchema = {
  type: "object",
  properties: {
    type: {
      type: "string",
      enum: [...GENERIC_SNIPPET_TYPES],
    },
    title: { type: "string" },
    items: {
      type: "array",
      items: {
        type: "object",
        properties: {
          label: { type: "string" },
          description: { type: "string" },
          color: { type: "string" },
          bullets: { type: "array", items: { type: "string" } },
          value: { type: "number" },
          fontSize: { type: "number" },
          bold: { type: "boolean" },
          textColor: { type: "string" },
        },
        required: ["label"],
      },
    },
    axisLabels: {
      type: "object",
      properties: {
        x: { type: "string" },
        y: { type: "string" },
      },
    },
    left: { type: "number" },
    top: { type: "number" },
    width: { type: "number" },
    height: { type: "number" },
    fontSize: { type: "number" },
    fontFamily: { type: "string" },
    textColor: { type: "string" },
    bold: { type: "boolean" },
    headerColor: {
      type: "string",
      description:
        "REQUIRED brand accent (hex from the live theme accent1 / title / divider colors in deckLayoutCatalog). Used as a thin strip/rule accent. Do not treat it as a flood fill, and do not derive a different fill for every item from it.",
    },
  },
  required: ["type", "items", "headerColor"],
};

/** @deprecated Use SNIPPET_SCHEMA */
const COMPOSITE_LAYOUT_SCHEMA = SNIPPET_SCHEMA;

export const OPENAI_MODIFICATION_JSON_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["mode"],
  properties: {
    mode: {
      type: "string",
      enum: ["chat", "edit"],
      description:
        "Discriminator selecting the response shape. Use \"chat\" for conversational answers (summarization, explanation, evaluation, Q&A) where the user did NOT ask for any slide change; populate `reply` and OMIT `slides` and `summary`. Use \"edit\" whenever the user asks to add/change/remove/format anything on the slide; populate `slides` (and optional `summary`) and OMIT `reply`. When ambiguous, prefer \"chat\" and offer in the `reply` to make the change.",
    },
    reply: {
      type: "string",
      description:
        "Chat-mode answer rendered as the assistant message bubble. REQUIRED when mode == \"chat\"; OMIT when mode == \"edit\". 1–3 sentences in plain prose, the user's voice. Answer the question directly, then optionally offer to make a related slide change (e.g. \"...want me to add a callout for that?\"). No JSON, no markdown headings, no bullet lists.",
    },
    summary: {
      type: "string",
      description:
        "Edit-mode USER-FACING summary of WHAT changed and WHY, in the user's voice. Used ONLY when mode == \"edit\". Renders as the header of the Applied-Changes card in chat. The per-shape mechanical details (\"Replaced X with Y\", \"Updated 6 shapes\", etc.) already render in the expandable body — do NOT repeat them here. Good: \"Personalized the Samsung deep-dive: updated the title and overview, refreshed recent news, and replaced financials with KRW figures.\" Bad: \"Updated 6 shapes\" / \"Modified text 4 times\" / \"Applied changes to slide 9\" / restating every shape or listing every replacement. Skip pixel positions, shape IDs, and counts. Aim for ~120–250 chars, no bullet lists. Omit (do not include the field) for true no-op turns and for purely structural turns where there is nothing meaningful to summarize. NEVER set in chat mode (use `reply` instead).",
    },
    slides: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "index", "shapes"],
        properties: {
          id: { type: "string" },
          index: { type: "integer" },
          summary: {
            type: "string",
            description:
              "Optional user-facing per-slide summary for create-slide/deck-build progress streaming. Describe what this slide draft will contain before the full edit payload finishes. Use drafting/preparing language, plain prose, no bullets, no shape ids, no pixel positions, no mechanical edit counts. Keep it short, roughly 80-180 chars. Only populate when the request path explicitly asks for per-slide summaries; omit in normal chat/edit flows.",
          },
          action: {
            type: "string",
            enum: ["modify", "insert", "duplicate", "delete"],
            description:
              "Slide operation. For an exact whole-slide copy, use duplicate with this slide id/index as the source and leave shapes empty unless the copy also needs edits; do not reconstruct an entire slide shape-by-shape.",
          },
          layoutName: {
            type: "string",
            description:
              "Optional for action=\"insert\": name of an existing deck custom layout to apply when creating the new slide. Choose from deckLayoutCatalog.layouts[*].layoutName.",
          },
          slideMasterName: {
            type: "string",
            description:
              "Optional for action=\"insert\": slide master containing layoutName. Choose from deckLayoutCatalog.layouts[*].slideMasterName when available.",
          },
          afterSlideId: {
            type: "string",
            description:
              "For action=\"duplicate\": existing slide id to place the copy AFTER (the insert anchor - the previously created slide, or the seed insert anchor for the first slide). slide.id is the SOURCE slide being duplicated, so use afterSlideId to control the copy's position. Omit to place the copy right after the source slide.",
          },
          shapes: {
            type: "array",
            items: SHAPE_MODIFICATION_SCHEMA,
          },
          citationIds: {
            type: "array",
            description:
              "Per-slide citation UUIDs. REQUIRED for every slide that places cited values. Each id MUST match an entry in the top-level citations[] array and only include citations used on THIS slide. citationIds MUST sit on the slide op that creates/fills the cited content - NEVER at the modifications root (a root-level citationIds array is ignored; modifications.citations[] is the pool, modifications.citationIds is invalid).",
            items: { type: "string" },
          },
          snippets: { type: "array", items: SNIPPET_SCHEMA },
          compositeLayout: COMPOSITE_LAYOUT_SCHEMA,
        },
      },
    },
    diagnostics: {
      type: "array",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["code", "category", "message"],
        properties: {
          code: { type: "string" },
          category: { type: "string" },
          message: { type: "string" },
          slideIndex: { type: "integer" },
          slideId: { type: "string" },
          shapeId: { type: "string" },
          action: { type: "string" },
        },
      },
    },
    citations: {
      type: "array",
      description:
        "Citations relevant to the modifications applied to the slide. citations[] is the TOP-LEVEL SOURCE OF TRUTH for which sources back this slide — inline <u id=\"…\"> markup is only an optional rendering hint for text shapes. Include ONE entry for every cited VALUE you actually placed on the slide, in ANY component: shape text, table cells, textReplacements, snippet labels/descriptions/bullets, snippet numeric values, chart titles, chart axis titles, chart series names, AND chart data point values. Declare the citation regardless of whether you wrapped the value in inline markup (chart data points and snippet numeric fields can never carry markup, but they still belong here). Do NOT put citations inside slides[] or any individual slide object. Do NOT pad slide text just to make an inline tag fit naturally — choose concise phrasing and rely on this array to record the source. If 5 cited values were available but only 3 were placed on the slide, emit only those 3 here. Reuse `id`, `dataSource`, and `type` EXACTLY as they appear in the source `<u …>` tag (from finster_query results or from the slide-generation grounding context); do NOT invent ids or change attributes. Omit (do not include the field) when no Finster value was placed on the slide.",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["id", "dataSource", "type", "value"],
        properties: {
          id: {
            type: "string",
            description:
              "UUID of the cited value. Must match the `id` attribute of a `<u id=\"…\" data-datasource=\"…\" data-type=\"…\">VALUE</u>` tag from a finster_query tool result or from the slide-generation grounding context.",
          },
          dataSource: {
            type: "string",
            description:
              "Source label for the citation, copied from the matching `<u …>` tag's `data-datasource` attribute (e.g. SEC_FILINGS, FACTSET_FUNDAMENTALS, COMPANY_WEBSITE, WEB_SEARCH).",
          },
          type: {
            type: "string",
            description:
              "Citation type, copied from the matching `<u …>` tag's `data-type` attribute (e.g. value, number, fact).",
          },
          value: {
            type: "string",
            description:
              "The displayed value placed on the slide and shown on the citation card. Prefer the concise slide-visible phrasing you used (e.g. \"$281.7B\", \"42%\", \"Microsoft Corp.\"). For values placed in non-text components (chart series values, snippet numeric values, etc.) where inline markup is impossible, emit the slide-visible VALUE here. It may be a concise or paraphrased version of the raw source span, but it must still correspond to the cited source id.",
          },
        },
      },
    },
  },
};

export const OPENAI_MODIFICATION_RESPONSE_FORMAT: JsonSchema = {
  type: "json_schema",
  name: "presentation_modification_v1",
  schema: OPENAI_MODIFICATION_JSON_SCHEMA,
  strict: false,
};

export const OPENAI_TERMINAL_CHAT_JSON_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["mode", "reply"],
  properties: {
    mode: {
      type: "string",
      enum: ["chat"],
      description:
        "Terminal output in agentic mode is chat-only. All PowerPoint edits must be performed through apply_edits before the final response.",
    },
    reply: {
      type: "string",
      description:
        "Final user-facing summary after any tool-based work. Plain prose, 1-3 sentences, no JSON and no markdown headings.",
    },
  },
};

export const OPENAI_TERMINAL_CHAT_RESPONSE_FORMAT: JsonSchema = {
  type: "json_schema",
  name: "terminal_chat_v1",
  schema: OPENAI_TERMINAL_CHAT_JSON_SCHEMA,
  strict: true,
};

function cloneJsonSchema<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T;
}

function textFieldCitationDescription(brand: BrandContext): string {
  return `On action="modify", full text replacement loses existing paragraph/run formatting. Never use it when observed paragraphs[] include bulletVisible:true; use textReplacements instead. Full text content of the shape. MAY optionally contain inline ${brand.citationMarkupLabel} of the form <u id="<uuid>" data-datasource="<DATA_SOURCE>" data-type="<TYPE>">VALUE</u> around values returned by ${brand.researchToolName} or present in the slide-generation grounding context. Inline markup is a per-shape rendering hint only — the runtime strips it before writing to PowerPoint, so it never affects what the user sees on the slide. Inline markup is NEVER a substitute for declaring the citation in the top-level citations[] array. Do NOT rephrase, pad, or expand slide text just to make an inline tag fit naturally; choose the phrasing the slide needs and declare the citation in citations[].`;
}

function replacementCitationDescription(brand: BrandContext): string {
  return `Replacement string. MAY optionally contain inline ${brand.citationMarkupLabel} <u id="…" data-datasource="…" data-type="…">VALUE</u> markup around values returned by ${brand.researchToolName} or present in the slide-generation grounding context. Inline markup is a per-shape rendering hint only — the runtime strips it before applying the replacement. Inline markup is NEVER a substitute for declaring the citation in the top-level citations[] array; declare the citation regardless of whether you wrapped the value inline.`;
}

function cellCitationDescription(brand: BrandContext): string {
  return `New cell text. MAY optionally contain inline ${brand.citationMarkupLabel} <u id="…" data-datasource="…" data-type="…">VALUE</u> citation markup copied from ${brand.researchToolName} output or from the slide-generation grounding context. Inline markup is a per-cell rendering hint only — the runtime strips it before writing the cell. Inline markup is NEVER a substitute for declaring the citation in the top-level citations[] array; declare the citation regardless of whether you wrapped the value inline.`;
}

function citationsFieldDescription(brand: BrandContext): string {
  return `Citations relevant to the modifications applied to the slide. citations[] is the TOP-LEVEL SOURCE OF TRUTH for which sources back this slide — inline <u id="…"> markup is only an optional rendering hint for text shapes. Include ONE entry for every cited VALUE you actually placed on the slide, in ANY component: shape text, table cells, textReplacements, snippet labels/descriptions/bullets, snippet numeric values, chart titles, chart axis titles, chart series names, AND chart data point values. Declare the citation regardless of whether you wrapped the value in inline markup (chart data points and snippet numeric fields can never carry markup, but they still belong here). Do NOT put citations inside slides[] or any individual slide object. Do NOT pad slide text just to make an inline tag fit naturally — choose concise phrasing and rely on this array to record the source. If 5 cited values were available but only 3 were placed on the slide, emit only those 3 here. Reuse \`id\`, \`dataSource\`, and \`type\` EXACTLY as they appear in the source <u …> tag (from ${brand.researchToolName} results or from the slide-generation grounding context); do NOT invent ids or change attributes. Omit (do not include the field) when no ${brand.publicBrandName} value was placed on the slide.`;
}

function citationIdDescription(brand: BrandContext): string {
  return `UUID of the cited value. Must match the \`id\` attribute of a <u id="…" data-datasource="…" data-type="…">VALUE</u> tag from a ${brand.researchToolName} tool result or from the slide-generation grounding context.`;
}

function buildOpenAIModificationJsonSchema(
  brand: BrandContext,
  brandId?: BrandId | null,
  toolPolicy?: ToolExecutionPolicy
): JsonSchema {
  const schema = cloneJsonSchema(OPENAI_MODIFICATION_JSON_SCHEMA);
  const properties = schema.properties as Record<string, unknown>;
  const slidesField = properties.slides as { items: { properties: Record<string, unknown> } };
  const slideProps = slidesField.items.properties;
  const shapesField = slideProps.shapes as { items: { properties: Record<string, unknown> } };
  const shapeProps = shapesField.items.properties;

  (shapeProps.text as { description: string }).description = textFieldCitationDescription(brand);
  const textReplacements = shapeProps.textReplacements as {
    items: { properties: { replace: { description: string } } };
  };
  textReplacements.items.properties.replace.description = replacementCitationDescription(brand);
  const tableField = shapeProps.table as {
    properties: {
      textUpdates: { items: { properties: { text: { description: string } } } };
    };
  };
  tableField.properties.textUpdates.items.properties.text.description =
    cellCitationDescription(brand);

  const citations = properties.citations as {
    description: string;
    items: { properties: { id: { description: string } } };
  };
  citations.description = citationsFieldDescription(brand);
  citations.items.properties.id.description = citationIdDescription(brand);

  if (brandId === "ubs") {
    const iconIds = getBrandIconIds("ubs");
    const iconDescription = buildBrandIconSchemaDescription("ubs");
    if (iconIds.length > 0 && iconDescription) {
      shapeProps.brandIconId = {
        type: "string",
        enum: [...iconIds],
        description: iconDescription,
      };
    }
  }

  const snippetPolicy = resolveSnippetPolicy({
    brandId,
    explicitEnabled: toolPolicy?.enableSnippetGuidance,
  });
  if (!snippetPolicy.enabled) {
    delete slideProps.snippets;
    delete slideProps.compositeLayout;
  } else {
    const snippetField = slideProps.snippets as { items: { properties: { type: { enum: string[] } } } };
    snippetField.items.properties.type.enum = [...snippetPolicy.typeEnum];
    if (slideProps.compositeLayout) {
      const compositeField = slideProps.compositeLayout as {
        properties: { type: { enum: string[] } };
      };
      if (compositeField.properties?.type) {
        compositeField.properties.type.enum = [...snippetPolicy.typeEnum];
      }
    }
  }

  return schema;
}

/** Test helper: build the apply_edits modifications schema for a brand. */
export function buildOpenAIModificationJsonSchemaForTesting(
  brand: BrandContext,
  brandId?: BrandId | null,
  toolPolicy?: ToolExecutionPolicy
): JsonSchema {
  return buildOpenAIModificationJsonSchema(brand, brandId, toolPolicy);
}

export function buildOpenAIModificationResponseFormat(
  _toolPolicy?: ToolExecutionPolicy
): JsonSchema {
  return OPENAI_TERMINAL_CHAT_RESPONSE_FORMAT;
}

const OPENAI_TOOL_DEFINITIONS: OpenAIFunctionToolDefinition[] = [
  {
    type: "function",
    name: "get_company_logo",
    description:
      "Fetch company logo availability for one or more disambiguated companies. Parse long legal-name lists into one canonical entry per company before calling: keep the legal/trading name including Inc., Corp., Ltd., plc, SE, and Holdings. Keep domain names when they are the brand. Strip only share-class / listing noise (Class A, Class B, Sponsored ADR). Never call this with fragments like Inc., Class A, Sponsored ADR, Holdings Inc., Design Systems Inc., or Technologies Inc. Examples: BILL Holdings, Inc. -> BILL Holdings, Inc.; Palantir Technologies Inc. Class A -> Palantir Technologies Inc.; Cadence Design Systems, Inc. -> Cadence Design Systems, Inc.; International Business Machines Corporation -> International Business Machines Corporation; SS&C Technologies Holdings, Inc. -> SS&C Technologies Holdings, Inc.; SAP SE -> SAP SE; monday.com Ltd. -> monday.com Ltd.; NICE Ltd. Sponsored ADR -> NICE Ltd. Returns candidate names, matched tickers, and opaque logoCandidateRef values per requested company. Choose only a candidate whose matchedCompanyName is clearly the same brand; use matchedTicker as supporting context only, not as sole evidence. Set logoCompanyName to result.companyName and logoCandidateRef to candidate.logoCandidateRef on the create shape. If none is reliable, use a short text name. Do not paste URLs. Use for named companies/brands on the slide, not only when the user asks for logos.",
    parameters: {
      type: "object",
      additionalProperties: false,
      required: ["company_names"],
      properties: {
        company_names: {
          type: "array",
          items: { type: "string" },
          minItems: 1,
          description:
            "Canonical company names to resolve in one call. Keep Inc./Corp./Ltd./Holdings/SE as part of the name. Include each disambiguated company once; omit duplicate names and false-positive fragments such as Inc., Class A, or Sponsored ADR by themselves. The result reports candidates and failures per company without failing the whole call.",
        },
        size: { type: "integer", minimum: 16, maximum: 2048 },
      },
    },
  },
  {
    type: "function",
    name: "finster_query",
    description:
      "Query Finster financial data for fundamentals, estimates, filings, transcripts, and related research context.",
    parameters: {
      type: "object",
      additionalProperties: false,
      required: ["query", "queryMode"],
      properties: {
        query: { type: "string" },
        queryMode: {
          type: "string",
          enum: ["standard", "thinking"],
          description:
            "Query depth. \"standard\" = fast single-pass lookup; use for a single fact/metric, one company, or a direct definition. \"thinking\" = slower, multi-step reasoning; use for comparisons across companies/periods, multi-part questions, or when the answer needs several lookups synthesized. Prefer standard for simple facts; escalate to thinking only when the question is genuinely complex.",
        },
      },
    },
  },
  {
    type: "function",
    name: "get_slide_images",
    description:
      "Capture slide images by displayed PowerPoint slide numbers for cross-slide visual matching and fidelity-sensitive edits.",
    parameters: {
      type: "object",
      additionalProperties: false,
      required: ["slide_numbers"],
      properties: {
        slide_numbers: {
          type: "array",
          items: { type: "integer", minimum: 0 },
          minItems: 1,
        },
        max_images: { type: "integer", minimum: 1, maximum: 10 },
      },
    },
  },
];

// Unified-agent primitives. apply_edits owns the full PresentationModification
// schema; terminal structured output is chat-only in agentic mode. get_slides_info
// is the observe tool (replaces get_slide_images and is never nerfed).
const APPLY_EDITS_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "apply_edits",
  description:
    "Required for ALL PowerPoint edits in agentic mode except enabled live FactSet ActiveGraph operations, which use their dedicated tool. Apply a batch of slide edits in one call: modify/delete/duplicate slides, insert new slides (optionally from a deck layout via slide.layoutName + slide.slideMasterName), and create/modify/delete shapes (text, tables, native charts, images, snippets). Do not return terminal mode=\"edit\" output for edits; terminal structured output is chat-only and cannot observe/refine. When inserting a layout slide, fill its placeholders in the SAME call by giving each fill shape a placeholderName taken from deckLayoutCatalog.layouts[*].placeholders[*].name (omit geometry) — the runtime inserts the slide, maps placeholderName to the real generated shape id, and applies your content as a modify so it FILLS the placeholder instead of overlaying a new shape. For existing slides, modify/delete shapes by their real shape id. Citations: whenever you place a cited value on a slide, declare it in the TOP-LEVEL modifications.citations[] array (a sibling of slides[]; each entry is { id, dataSource, type, value }) AND list that id in the slide's citationIds[]. Every id in any slides[].citationIds MUST have a matching citations[] entry in the SAME apply_edits call; citations placed inside a slide object (slides[].citations) are invalid and ignored. Returns an apply summary (applied/skipped/errors with diagnostics); for insert/duplicate it also returns the created slides' re-extracted JSON and screenshots so you can verify and refine. Set return.json/return.image to also receive re-extracted layout/screenshots for modify-only edits.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: ["modifications"],
    properties: {
      modifications: OPENAI_MODIFICATION_JSON_SCHEMA,
      return: {
        type: "object",
        additionalProperties: false,
        properties: {
          json: { type: "boolean" },
          image: { type: "boolean" },
        },
      },
    },
  },
};

const GET_SLIDES_INFO_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "get_slides_info",
  description:
    "Observe slides: returns the re-extracted layout JSON (true current geometry, shape ids, and text), screenshots, and/or detailed deck layout metadata. Use to verify layout, inspect placeholders, detect overlaps/clipping, and check cross-slide consistency after edits. Reads ANY slide(s) by number (a batch is allowed), not just the loaded deckContext window; pass image:true for a screenshot of any slide. For a cross-slide copy or style/format match, observe the SOURCE slide(s) with json:true AND image:true before applying the edit.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: [],
    properties: {
      slide_numbers: {
        type: "array",
        items: { type: "integer", minimum: 0 },
        description:
          "Displayed PowerPoint slide numbers to observe - ANY slide(s) in the deck (a batch is allowed), not just the loaded window. Optional when only requesting layout/design metadata.",
      },
      json: { type: "boolean" },
      image: { type: "boolean" },
      layouts: {
        type: "boolean",
        description:
          "When true, return detailed custom layout metadata beyond the deterministic deckLayoutCatalog.",
      },
      layout_names: {
        type: "array",
        items: { type: "string" },
        description: "Optional custom layout names to inspect in detail.",
      },
      designs: {
        type: "boolean",
        description: "When true, include design font/color/text-style metadata when available.",
      },
      master_shapes: {
        type: "boolean",
        description: "When true, include master/static shape metadata when available.",
      },
    },
  },
};

const INSERT_LIBRARY_SLIDE_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "insert_library_slide",
  description:
    "Insert a pre-built slide from the slide library into the deck at a specific position. Call this after Create slides when that approved slide's selection is LIBRARY (librarySlideId), or on a direct create/build with no Review plan. apply_edits CANNOT import a library slide. Set after_slide_id to the real id of the slide you want this inserted AFTER; omit it only for the very first slide when no anchor exists. The runtime fetches the library slide's PPTX and inserts it with its original formatting, and returns the new slide's id and index (and a screenshot). After inserting, FILL the new slide in a follow-up apply_edits (modify by the returned id); preserve the library slide's layout and design - do not rebuild it.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: ["library_slide_id"],
    properties: {
      library_slide_id: {
        type: "string",
        description:
          "The librarySlideId from the approved plan's per-slide selection for this slide.",
      },
      after_slide_id: {
        type: "string",
        description:
          "Real slide id to insert the library slide AFTER (the previously created slide). Omit only for the first slide when there is no anchor.",
      },
    },
  },
};

const FETCH_PRESLINK_FILES_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "apply_preslink",
  description:
    'Read or update the FactSet PresLink workbooks behind slide charts and tables. Choose operation:"refresh" whenever the request changes WHICH data the slide shows - a different company or ticker, a different peer set, a different period or as-of date, or otherwise re-sourced table and chart values - even when the user never says "PresLink". The generic pattern is: change/update/refresh + a slide, page, or a specific chart or table on it + a new entity, peer group, period, or as-of date; that shape is a refresh whether or not the request names the value being replaced. Choose operation:"inspect" for questions about the linked source workbook or its underlying values. Do not call this tool at all when the request only changes wording, colour, layout, size, or formatting (for example "make the title bold"), or is otherwise unrelated to linked data. Refresh runs alone, replaces slides, and returns fresh ids for later native edits. The host verifies which candidate shapes are actually linked.',
  parameters: {
    type: "object",
    additionalProperties: false,
    required: ["operation", "shapes"],
    properties: {
      operation: {
        type: "string",
        enum: ["refresh", "inspect"],
        description:
          'Intent for this call. "refresh" updates linked workbooks/slides, "inspect" reads linked source details without mutation.',
      },
      shapes: {
        type: "array",
        minItems: 1,
        description:
          'Candidate shapes from the requested target, using ids from the current slide context. For an unspecified "this", pass all plausible shapes on the selected slide and let the host verify which are linked.',
        items: {
          type: "object",
          additionalProperties: false,
          required: ["slide_id", "shape_id"],
          properties: {
            slide_id: { type: "string", description: "The slide's id (slideId) from the slide context." },
            shape_id: { type: "string", description: "The shape's id from the slide context." },
          },
        },
      },
      include_ppt_file: {
        type: "boolean",
        description: "When true, also fetch and report the presentation archive metadata. Default false.",
      },
      scope: {
        type: "string",
        enum: ["shapes", "deck"],
        description:
          'Use "deck" only for an explicit all-slides/whole-deck request. Otherwise use "shapes" and pass candidates from the intended slide(s). Default "shapes".',
      },
    },
  },
};

const DECK_PLAN_SLIDE_ITEM_SCHEMA: JsonSchema = {
  type: "object",
  additionalProperties: false,
  required: ["index", "title", "bullets"],
  properties: {
    index: { type: "integer" },
    title: {
      type: "string",
      description: "Conclusion title for this slide (the one argument). Not a topic label only.",
    },
    bullets: {
      type: "array",
      items: { type: "string" },
      description:
        "Evidence outline for this slide's title, not a takeaway paraphrase. Enumerate grounded facts (figures, named entities, dates): one row or entity per bullet, or a tight cluster of related figures. Not a three-item summary and not layout instructions.",
    },
    baseSlideIndex: {
      anyOf: [{ type: "integer", minimum: 0 }, { type: "null" }],
      description:
        "0-based existing deck slide to duplicate as the template. Null/omit if none.",
    },
    librarySlideId: {
      anyOf: [{ type: "string" }, { type: "null" }],
      description:
        "Selected library template for this slide (the Review top pick). Set only when a previewed catalog item's layout/structure fits this slide's content. Omit on present_deck_plan for FREEFORM / Agent builds from scratch when none fits, the catalog is empty / named collection is missing, or the user asked for from-scratch — still send librarySlideCandidates. On update_deck_plan patches, omit the field to keep the current pick; pass an empty string or null to clear it to FREEFORM.",
    },
    librarySlideTitle: { type: "string" },
    librarySlideThumbnailUrl: { type: "string" },
    librarySlideTags: { type: "array", items: { type: "string" } },
    librarySlideDescription: { type: "string" },
    librarySlideCandidates: {
      type: "array",
      description:
        "Up to 3 previewed catalog suggestions for this slide (Review Match 1/2/3). Always set when search_slide_library returned items, including when librarySlideId is omitted so FREEFORM is the selected pick.",
      items: {
        type: "object",
        additionalProperties: false,
        required: ["slideId", "title"],
        properties: {
          slideId: { type: "string" },
          title: { type: "string" },
          description: { type: "string" },
          tags: { type: "array", items: { type: "string" } },
          thumbnailUrl: { type: "string" },
          rankReason: { type: "string" },
        },
      },
    },
  },
};

const LIST_SLIDE_LIBRARY_COLLECTIONS_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "list_slide_library_collections",
  description:
    "List personal and company slide-library collections. Marks isDefault on the company default collection (name match \"Company default collection\", else the first READY company collection). Use this to resolve a collection the user named. Default browse should use the company default collection, not a personal one.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: [],
    properties: {},
  },
};

const SEARCH_SLIDE_LIBRARY_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "search_slide_library",
  description:
    "Page READY slides in one or more slide-library collections. Default browse: omit collection_ids and omit scope to use the company default collection. If there is no company default, the host falls back to all insertable slides (every company collection plus the personal library). Pass scope:\"all\" ONLY when the user asks to pick from all slides / the whole library — not because the requested page topic sounds like a collection. Pass several company collection ids from list_slide_library_collections to union those catalogs in one call (deduped by slide_id). Returns metadata plus thumbnailUrl strings (no image bytes). Paginate until hasMore is false, then shortlist from the catalog. Optional tags filter when the label is already obvious from a prior catalog result; do not invent tags from the requested topic. Personal collections only when the user named that collection. Never pass collection_ids: [\"__all__\"]; use scope:\"all\" only when the user asked for the whole library.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: [],
    properties: {
      collection_ids: {
        type: "array",
        items: { type: "string" },
        description:
          "Collection ids to search. Omit (and omit scope) to use the company default collection (or all insertable slides if there is no default). Multiple ids are unioned and deduped by slide_id. Ignored when scope is \"all\". Personal collection ids only when the user named that collection.",
      },
      scope: {
        type: "string",
        enum: ["all"],
        description:
          "Search every insertable library slide (all company collections plus the personal All-tab library). Use ONLY when the user asks for all slides / the whole library. Do not use this because the requested slide topic sounds like a collection name. Do not pass collection_ids with this scope.",
      },
      tags: { type: "array", items: { type: "string" } },
      offset: { type: "integer", minimum: 0 },
      limit: {
        type: "integer",
        minimum: 1,
        maximum: 200,
        description: "Page size. Defaults to 100 if omitted.",
      },
    },
  },
};

const GET_LIBRARY_SLIDE_PREVIEWS_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "get_library_slide_previews",
  description:
    "Fetch rendered thumbnails for library slides so you can SEE candidates before committing a template.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: ["slide_ids"],
    properties: {
      slide_ids: {
        type: "array",
        minItems: 1,
        maxItems: 200,
        items: { type: "string" },
      },
    },
  },
};

const PRESENT_DECK_PLAN_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "present_deck_plan",
  description:
    "Show the Review Slide Outline popup when the user explicitly asks to plan, outline, or review a plan first, or the request says to use the planning flow — including one slide, a fully specified slide set, an exact library precedent, and a named collection. Resolve/browse and preview viable library precedents, preselect the best structural match for each slide, then present the plan so the user can confirm, switch candidates, or choose FREEFORM. Never use when the request only edits existing slides or exclusively copies/repurposes slides from the current deck; those use direct apply_edits duplication. Once Review is open, do not call apply_edits or insert_library_slide until the user confirms Create slides.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: ["topic", "slides"],
    properties: {
      topic: { type: "string" },
      slides: {
        type: "array",
        minItems: 1,
        items: DECK_PLAN_SLIDE_ITEM_SCHEMA,
      },
    },
  },
};

const UPDATE_DECK_PLAN_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "update_deck_plan",
  description:
    "Update the unconfirmed Review Slide Outline. Does not edit PowerPoint. Pass slides to replace the full outline, or patches to change specific slides by index (title, bullets, librarySlideId, baseSlideIndex, candidates). To switch a slide to FREEFORM / Agent builds from scratch, patch librarySlideId as an empty string and baseSlideIndex as null; leave librarySlideCandidates unchanged. To use another listed librarySlideCandidates match, patch that librarySlideId only (do not search). Search first only when the requested template is not in that list. Omitting those fields keeps the current pick. Use only while a plan is open in Review.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: [],
    properties: {
      topic: { type: "string" },
      slides: {
        type: "array",
        items: DECK_PLAN_SLIDE_ITEM_SCHEMA,
      },
      patches: {
        type: "array",
        description:
          "Sparse per-slide updates. Omit title and bullets to keep the current outline copy (library-only patches should send index + librarySlideId only).",
        items: {
          type: "object",
          additionalProperties: false,
          required: ["index"],
          properties: {
            ...(DECK_PLAN_SLIDE_ITEM_SCHEMA.properties as Record<string, unknown>),
          },
        },
      },
    },
  },
};

function buildActiveGraphToolDefinition(): OpenAIFunctionToolDefinition {
  const chartParameters = buildFactsetChartParametersSchema();
  return {
    type: "function",
    name: "active_graph",
    description:
      "Create, inspect, resize, or delete a live FactSet ActiveGraph OLE in PowerPoint. " +
      "Use complete chartParameters for create. Never provide XML. " +
      "chartFormat colors are RGB(r,g,b): seriesColor0… for series; fontColor.heading1FontColor/heading2FontColor/legendFontColor and axisColor for title/legend/axis chrome. " +
      "Omit chartFormat to keep FactSet defaults (blue series and heading, grids on). The runtime does not inject brand/deck colors. " +
      "titleOverride on create attempts to set OLE Heading1Text; heading1FontColor and heading1FontHeight only style it. " +
      "stylesetId is a FactSet skin, not a bank brand. PowerPoint cannot restyle an existing OLE's colors; recreate to change colors.",
    parameters: {
      type: "object",
      additionalProperties: false,
      required: ["operation"],
      properties: {
        operation: {
          type: "string",
          enum: [
            "create_active_graph",
            "inspect_selected_active_graph",
            "list_active_graph_stylesets",
            "update_active_graph_frame",
            "delete_selected_active_graph",
          ],
        },
        chartParameters,
        titleOverride: {
          type: "string",
          minLength: 1,
          maxLength: 200,
          description:
            "OLE main-title text (Heading1Text) for create_active_graph only. " +
            "Omit to keep the Chart Creator default heading. Some templates have no Heading1Text target: " +
            "create then returns a warning and keeps its default title. " +
            "Style the title with chartFormat.fontColor.heading1FontColor (Chart text color source, not seriesColor0) and chartFormat.heading1FontHeight.",
        },
        target: {
          type: "object",
          additionalProperties: false,
          properties: {
            slideId: {
              type: "string",
              minLength: 1,
              description:
                "Real PowerPoint slide ID for create_active_graph only. Existing-chart operations require the user's current single-shape selection.",
            },
            left: { type: "number", minimum: 0 },
            top: { type: "number", minimum: 0 },
            width: { type: "number", exclusiveMinimum: 0 },
            height: { type: "number", exclusiveMinimum: 0 },
          },
        },
      },
    },
  };
}

const LIST_ACTIVE_GRAPH_TYPES_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "list_active_graph_types",
  description:
    "List the FactSet ActiveGraph chart types enabled for this PowerPoint user and host. Use when the requested metric/type is unnamed or uncertain.",
  parameters: {
    type: "object",
    additionalProperties: false,
    properties: {},
  },
};

const ACTIVE_GRAPH_ID_LOOKUP_TOOL_DEFINITION: OpenAIFunctionToolDefinition = {
  type: "function",
  name: "id_lookup",
  description:
    "Resolve one company name or ticker to authoritative FactSet candidates. Consume the result in a later hop; never silently choose an ambiguous listing or share class.",
  parameters: {
    type: "object",
    additionalProperties: false,
    required: ["search_text"],
    properties: {
      search_text: {
        type: "string",
        minLength: 1,
        maxLength: 512,
        description: "Company name or identifier, limited to 512 UTF-8 bytes.",
      },
    },
  },
};

const OPENAI_AGENTIC_TOOL_DEFINITIONS: OpenAIFunctionToolDefinition[] = [
  ...OPENAI_TOOL_DEFINITIONS.filter((tool) => tool.name !== "get_slide_images"),
  APPLY_EDITS_TOOL_DEFINITION,
  GET_SLIDES_INFO_TOOL_DEFINITION,
  INSERT_LIBRARY_SLIDE_TOOL_DEFINITION,
  LIST_SLIDE_LIBRARY_COLLECTIONS_TOOL_DEFINITION,
  SEARCH_SLIDE_LIBRARY_TOOL_DEFINITION,
  GET_LIBRARY_SLIDE_PREVIEWS_TOOL_DEFINITION,
  PRESENT_DECK_PLAN_TOOL_DEFINITION,
  UPDATE_DECK_PLAN_TOOL_DEFINITION,
  FETCH_PRESLINK_FILES_TOOL_DEFINITION,
];

export function buildOpenAINativeTools(
  toolPolicy?: ToolExecutionPolicy
): OpenAIFunctionToolDefinition[] {
  const brand = getBrandContext(toolPolicy);
  // Agentic agent: apply_edits + get_slides_info; get_slide_images is removed
  // and get_slides_info is never gated (un-nerfed perception).
  const tools = OPENAI_AGENTIC_TOOL_DEFINITIONS.filter(
    (tool) => tool.name !== "apply_preslink" || toolPolicy?.allowPreslink === true
  );
  if (toolPolicy?.allowActiveGraph === true) {
    tools.push(
      buildActiveGraphToolDefinition(),
      LIST_ACTIVE_GRAPH_TYPES_TOOL_DEFINITION,
      ACTIVE_GRAPH_ID_LOOKUP_TOOL_DEFINITION,
    );
  }
  const hasSelectedUserFiles =
    normalizeSelectedUserFileIds(toolPolicy?.userFileIds ?? []).length > 0;
  return tools.map((tool) => {
    if (tool.name === "finster_query") {
      return {
        ...tool,
        name: brand.researchToolName,
        description: hasSelectedUserFiles
          ? `Query ${brand.researchToolDescriptionBrand} research sources and the selected Your Files for this request. When files are selected, use this as the default first step for new factual slide content, summaries, synthesis, extraction, or analysis unless the user explicitly asks to use current slide/deck content.`
          : `Query ${brand.researchToolDescriptionBrand} financial data for fundamentals, estimates, filings, transcripts, and related research context.`,
      };
    }
    if (tool.name === "apply_edits") {
      return {
        ...tool,
        parameters: {
          ...tool.parameters,
          properties: {
            ...(tool.parameters.properties as Record<string, unknown>),
            modifications: buildOpenAIModificationJsonSchema(brand, toolPolicy?.brandId, toolPolicy),
          },
        },
      };
    }
    return { ...tool };
  });
}

